import { useEffect, useLayoutEffect, useState } from "react";
import * as yup from "yup";
import { appConfig } from "../../../configs";

import {
  useCheckoutPage,
  useForceUpdate,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useNotifications,
} from "../../../hooks";
import {
  IViewHostedPaymentLink,
  IInitPaymentLinkDirectCardChargeRequest,
  IInitDirectCardChargeResponse,
  ISwAppTransactionsApi,
  IViewTransaction,
} from "../../../interfaces-and-types";
import {
  cardExpiryDateMask,
  // card_numberMask,
  globalHandlerUtils,
} from "../../../utils";
import { IIFrameRedirectMessage } from "../TransactionIFrame/TransactionIFrame";

import { View } from "./View";
import { transactionType } from "../../../enums";

interface IDirectCardChargeFormProps {
  isPreviewMode: boolean;
  tx_ref: string | null;
  isActive: boolean;
  isLineItemQtyInputTempDisabled: boolean;

  isSuccessHostedPaymentLink: boolean;
  hostedPaymentLink: IViewHostedPaymentLink;
  showSuccessPage: () => void;
  openInIframe: (url: string) => void;
}

export interface IHasExpiryDateHelper {
  expiryDateHelper: string | null;
}

export type TCardAuthType = "pin" | "avs_noauth" | "otp" | "redirect";

const performSuccessEvents = (data: IIFrameRedirectMessage) => {
  // get the tx from API and send to client modal

  setTimeout(() => {
    (window as any).parent.postMessage(
      {
        switchappId: "switchapp-inline-message",
        action: "callFunction",
        targetFunction: "onSuccess",
        params: data, // "payment.successful",
      },
      "*"
    );

    if (data.merchant_redirect_url)
      window.location.href = `${data.merchant_redirect_url}?tx_ref=${data.tx_ref}&status=${data.status}`;
  }, appConfig.merchantRedirectDelayMs); // redirect to merchant after a brief delay
};

const performFailedEvents = (data: IIFrameRedirectMessage) => {
  // get the tx from API and send to client modal

  (window as any).parent.postMessage(
    {
      switchappId: "switchapp-inline-message",
      action: "callFunction",
      targetFunction: "onFailure",
      params: data, // "payment.failed",
    },
    "*"
  );
};

export const DirectCardChargeForm = (props: IDirectCardChargeFormProps) => {
  const {
    isPreviewMode,
    isActive,
    isLineItemQtyInputTempDisabled,
    isSuccessHostedPaymentLink,
    hostedPaymentLink,
    openInIframe,
    showSuccessPage,
  } = props;

  const {
    isInitialized,
    lockCheckoutItems,
    country,
    setCountry,
    customer,
    setCustomer,
    setSaleTotal,
    setSaleCurrency,
    searchLineItemsText,
    line_items,
    setLineItems,
  } = useCheckoutPage();

  // tx has been completed either in success or failure
  const [transactionCompleted, setTransactionCompleted] = useState(false);

  const ResetForm = () => {
    reset({
      tx_ref: hostedPaymentLink.tx_ref,
      sw_app_tx_ref: hostedPaymentLink.sw_app_tx_ref,
      sw_app_payment_link_url: hostedPaymentLink.link_url,
      line_items: isInitialized
        ? line_items
        : hostedPaymentLink.line_items.map((item) => ({
            product: item.product,
            qty: item.qty,
          })),
      country,
      customer: isInitialized ? customer : formValues.customer,
    });
  };

  useLayoutEffect(() => {
    if (isSuccessHostedPaymentLink && hostedPaymentLink) {
      // console.log("resetting checkout form...");
      ResetForm();
    }
  }, [isSuccessHostedPaymentLink]);

  useLayoutEffect(() => {
    if (isPreviewMode && hostedPaymentLink) {
      // console.log("resetting preview form...");
      ResetForm();
    }
  }, [isPreviewMode, isInitialized]);

  // const forceUpdate = useForceUpdate([]);
  const forceUpdate = useForceUpdate();
  const notifications = useNotifications();

  const apiEndpointToCall: keyof ISwAppTransactionsApi =
    hostedPaymentLink.is_top_up_payment_link
      ? "initTopUpDirectCardChargeTransaction"
      : "initPaymentLinkDirectCardChargeTransaction";

  const {
    isLoading: isLoadingInitDirectCardCharge,
    isSuccess: isSuccessInitDirectCardCharge,
    isError: isErrorInitDirectCardCharge,
    error: directCardChargeApiError,
    data: directCardChargeApiResponse,
    mutate: directCardCharge,
  } = useMerchantApiMutation<
    IInitPaymentLinkDirectCardChargeRequest,
    IInitDirectCardChargeResponse
  >(apiEndpointToCall).usePostMutation;

  const [authInputErrorMessage, setAuthInputErrorMessage] = useState<
    null | string
  >(null);

  const [card_auth_type, setCardAuthType] = useState<null | TCardAuthType>(
    null
  );

  useEffect(() => {
    if (isSuccessInitDirectCardCharge) {
      console.log("directCardChargeApiResponse: ", directCardChargeApiResponse);

      const postResponseData = directCardChargeApiResponse.data;

      // show PIN input
      if (postResponseData.status !== "successful") {
        if (postResponseData.card_auth_required) {
          setValue("card_auth.card_auth_type", postResponseData.card_auth_type);
          setCardAuthType(postResponseData.card_auth_type as TCardAuthType);

          setTransactionCompleted(false); // tx might have been reinitialized

          if (
            postResponseData.card_auth_type === "redirect" &&
            postResponseData.card_auth_redirect_url
          ) {
            // clear redirectAuth: ensures form can still be resubmitted to backend as new init if redirect view is closed
            setValue("card_auth.card_auth_type", null);

            openInIframe(postResponseData.card_auth_redirect_url);
          }
        } else {
          // alert error
          // setTransactionCompleted(true);
          alert("An error occured: could not charge card");
        }
      } else {
        setTransactionCompleted(true);
        showSuccessPage();
      }
    }
  }, [isSuccessInitDirectCardCharge]);

  useEffect(() => {
    if (isErrorInitDirectCardCharge) {
      console.log("directCardChargeApiError: ", directCardChargeApiError);
      // setAuthInputErrorMessage(directCardChargeApiError);

      const { message = "Charge could not be completed." } =
        directCardChargeApiError?.data as any;

      notifications.showNotification({
        title: "Error",
        message,
      });
    }
  }, [isErrorInitDirectCardCharge]);

  // query backend and inform if embedded in iframe
  const {
    // isLoading: isLoadingRefetchTransaction,
    // isError: isErrorRefetchTransaction,
    isSuccess: isSuccessRefetchTransaction,
    data: transaction,
    // refetch: refetchTransaction,
  } = useMerchantApiQueryWithParams<
    { tx_ref: string; tx_type: transactionType },
    IViewTransaction
  >(
    [
      "getTransactionByRef",
      {
        tx_ref: props.tx_ref!, // NOTE: use this to ensure that react query caches with the correct queryKey
        tx_type: hostedPaymentLink.is_top_up_payment_link
          ? transactionType.topUp
          : transactionType.payment,
      },
    ],
    {
      enabled: transactionCompleted,
      refetchInterval: 6000, // refetch every 6 seconds
    }
    // (urlTemplate) => urlTemplate.replace("tx_ref", props.tx_ref!)
  );

  // this comes into effect when tx is in processing state
  useEffect(() => {
    if (isSuccessRefetchTransaction) {
      switch (transaction?.status) {
        case "successful":
          performSuccessEvents({
            channel: "switchAppRedirectMessage",
            merchant_redirect_url: transaction.redirect_url,
            status: transaction.status,
            tx_ref: transaction.tx_ref,
          });
          break;

        case "failed":
          performFailedEvents({
            channel: "switchAppRedirectMessage",
            merchant_redirect_url: transaction.redirect_url,
            status: transaction.status,
            tx_ref: transaction.tx_ref,
          });
          break;
      }
    }
  }, [isSuccessRefetchTransaction]);

  const initialFormValues: IInitPaymentLinkDirectCardChargeRequest &
    IHasExpiryDateHelper = {
    // refs
    tx_ref: null,
    sw_app_tx_ref: null,

    // payment link details
    sw_app_payment_link_url: "",

    // sale
    line_items: [],

    //card details
    country,
    cardholder_name: "",
    card_number: "",
    expiryDateHelper: null,
    expiry_month: null,
    expiry_year: null,
    cvv: "",
    first_name: "",
    last_name: "",

    card_auth: {
      card_auth_type: null,
      otp: null,
      pin: null,
      billing_zip: null,
      billing_city: null,
      billing_address: null,
      billing_state: null,
      billing_country: null,
    },

    customer: {
      full_name: "",
      email: "",
      phone_number: "",
    },
  };

  // TODO: add payment link requirements to the form eg customer address, tax, etc
  const directCardChargeFormSchema: yup.SchemaOf<
    IInitPaymentLinkDirectCardChargeRequest & IHasExpiryDateHelper
  > = yup
    .object({
      tx_ref: yup.string().required("tx_ref is required"),
      sw_app_tx_ref: yup.string().required("sw_app_tx_ref is required"),
      sw_app_payment_link_url: yup
        .string()
        .required("Payment link url is required"),

      line_items: yup.array().of(
        yup.object().shape({
          product: yup.string().required("product is required"),
          qty: yup
            .number()
            // .min(min_qty, "Min quantity must be greater than or equal to min")
            // .max(max_qty, "Max quantity must be less than or equal to {0}")
            .required("Qty is required"),
        })
      ),
      // .minlength(0, "The sale total must be greater than 0"),

      country: yup.string().required("Country is required"),
      cardholder_name: yup.string().required("Name on card is required"),
      card_number: yup
        .string()
        .transform((value) => value?.replace(" ", ""))
        .min(16, "Card number is invalid")
        // .matches(/^[0-9]+$/, "Must be only digits")
        .required("Card number is required"),
      expiryDateHelper: yup
        .string()
        // display date invalid when format incorrect
        .when("expiry_year", {
          is: (expiry_year: string) => !expiry_year || expiry_year.length < 2,
          then: yup.string().required("Expiry year is required"),
        })
        .when("expiry_month", {
          is: (expiry_month: string) =>
            !expiry_month || expiry_month.length < 2,
          then: yup.string().required("Expiry month is required"),
        })
        .required("Expiry date is required"),
      expiry_month: yup
        .string()
        .nullable()
        // .min(2, "Expiry month is invalid")
        // .transform((value) => (value < 1 ? `0${value}` : value))
        .required("Expiry month is required"),
      expiry_year: yup
        .string()
        .nullable()
        // .min(2, "Expiry year is invalid")
        .required("Expiry year is required"),
      cvv: yup.string().required("cvv is required"),
      first_name: yup.string().nullable().optional(),
      last_name: yup.string().nullable().optional(),

      card_auth: yup.object({
        // card_auth_type: PIN, AVS, OTP, 3DS
        card_auth_type: yup.string().nullable().optional(),
        // security: OTP
        otp: yup.string().nullable().optional(),
        // security: PIN
        pin: yup.string().nullable().optional(),
        // security: AVS
        billing_zip: yup.string().nullable().optional(),
        billing_city: yup.string().nullable().optional(),
        billing_address: yup.string().nullable().optional(),
        billing_state: yup.string().nullable().optional(),
        billing_country: yup.string().nullable().optional(),
      }),

      customer: yup
        .object({
          // email: yup.string().email().required("Customer Email is required"),
          email: yup
            .string()
            .email()
            .nullable()
            .optional()
            .when("sw_app_payment_link_url", {
              is: (_: string) =>
                !hostedPaymentLink.is_temp_payment_link &&
                !hostedPaymentLink.is_top_up_payment_link,
              then: yup.string().required("Customer Email is required"),
            }),
          full_name: yup.string().nullable().optional(),
          address: yup.string().nullable().optional(),
          phone_number: yup.string().nullable().optional(),
        })
        .required("Customer details are required"),
    })
    .required();

  const handleApiSubmit = (data: IInitPaymentLinkDirectCardChargeRequest) => {
    lockCheckoutItems(); // no more qty editing
    !isPreviewMode && directCardCharge(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { control, onFormSubmit, errors, register, reset, watch, setValue } =
    useHookForm({
      initialFormValues: initialFormValues,
      validationSchema: directCardChargeFormSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    });

  const formValues = watch();

  const saleTotal = formValues.line_items.reduce((acc, item) => {
    const plLineItem = hostedPaymentLink.line_items.find(
      (plItem) => plItem.product === item.product
    );

    return acc + item.qty * plLineItem!.price;
  }, 0);

  useEffect(() => {
    const monthAndYear = cardExpiryDateMask.transform(
      formValues.expiryDateHelper
    );

    setValue("expiry_month", monthAndYear[0] && Number(monthAndYear[0]));
    setValue("expiry_year", monthAndYear[1] && Number(monthAndYear[1]));
  }, [formValues.expiryDateHelper]);

  // TODO: remove crude implementation:
  // reason for implemenation: closure bug? that restores previous values on clicking submit
  useLayoutEffect(
    () => forceUpdate(),
    [
      formValues.country,

      formValues.customer.email,
      formValues.customer.full_name,
      formValues.customer.address,
      formValues.customer.phone_number,

      formValues.expiryDateHelper,

      formValues.cardholder_name,
      formValues.card_number,
      formValues.expiry_month,
      formValues.expiry_year,
      formValues.cvv,
    ]
  );

  // update sale lineitems & sale details across forms
  useLayoutEffect(() => {
    setCountry(formValues.country);
    setLineItems(formValues.line_items);
    setCustomer(formValues.customer);
    setSaleTotal(saleTotal);
    setSaleCurrency(hostedPaymentLink.currency);
  }, [formValues]);

  return (
    <View
      isActive={isActive}
      searchLineItemsText={searchLineItemsText}
      isLineItemQtyInputTempDisabled={isLineItemQtyInputTempDisabled}
      setValue={setValue}
      saleTotal={saleTotal}
      hostedPaymentLink={hostedPaymentLink}
      card_auth_type={card_auth_type}
      formValues={formValues}
      setPinValue={(pin: string) => setValue("card_auth.pin", pin)}
      authInputErrorMessage={authInputErrorMessage}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      control={control}
    />
  );
};
